import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import LoaderDashboard from './components/LoaderDashboard';
import NotificationBell, { LotNotificationListener } from './components/NotificationBell';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './context/ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import { usePushNotifications } from './hooks/usePushNotifications';
import IosInstallPrompt from './components/IosInstallPrompt';
import MarqueeBanner from './components/MarqueeBanner';
import BroadcastModal from './components/BroadcastModal';
import PartyMotivationBanner from './components/PartyMotivationBanner';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const GhausiaCollection = lazy(() => import('./pages/GhausiaCollection'));
const PartyLedger = lazy(() => import('./pages/PartyLedger'));
const Parties = lazy(() => import('./pages/Parties'));
const Payments = lazy(() => import('./pages/Payments'));
const RateCalculations = lazy(() => import('./pages/RateCalculations'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const UserApprovals = lazy(() => import('./pages/UserApprovals'));
const SuperAdminApprovals = lazy(() => import('./pages/SuperAdminApprovals'));
const ReviewLots = lazy(() => import('./pages/ReviewLots'));
const PersonalKhata = lazy(() => import('./pages/PersonalKhata'));
const PersonalKhataAccount = lazy(() => import('./pages/PersonalKhataAccount'));
const PersonalKhataShared = lazy(() => import('./pages/PersonalKhataShared'));
const UpgradeAccount = lazy(() => import('./pages/UpgradeAccount'));

function PersonalKhataAccessibleRoute({ sidebarOpen, setSidebarOpen }) {
  const { isAuthenticated } = useAuth();
  const body = <PersonalKhata standalone={!isAuthenticated} />;
  return isAuthenticated ? (
    <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
      {body}
    </Layout>
  ) : (
    <div className="pk-standalone-shell">{body}</div>
  );
}

function Layout({ children, sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }
      
      if (isAdmin && e.altKey) {
        if (e.code === 'KeyA') {
          e.preventDefault();
          if (location.pathname === '/ghausia') {
            window.dispatchEvent(new Event('open-new-lot'));
          } else {
            navigate(`/ghausia?action=new&t=${Date.now()}`);
          }
        } else if (e.code === 'KeyP' || e.code === 'KeyR') {
          e.preventDefault();
          if (location.pathname === '/payments') {
            window.dispatchEvent(new Event('open-new-payment'));
          } else {
            navigate(`/payments?action=new&t=${Date.now()}`);
          }
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isAdmin, location.pathname, navigate]);





  useEffect(() => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      setSidebarOpen(false);
    }
  }, [location.pathname, setSidebarOpen]);

  useEffect(() => {
    document.body.style.overflow =
      sidebarOpen && window.matchMedia('(max-width: 768px)').matches ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const isKhataRoute = location.pathname.startsWith('/personal-khata');

  return (
    <div className={`app-shell${sidebarOpen ? ' sidebar-open' : ''}`}>
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div
        className="app-sidebar-overlay"
        aria-hidden="true"
        onClick={() => setSidebarOpen(false)}
      />

      <button
        type="button"
        className="app-menu-btn"
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
        >
          {sidebarOpen ? (
            <path d="M6 18L18 6M6 6l12 12" />
          ) : (
            <>
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
      </button>

      <div
        className="app-top-actions"
        style={{ position: 'fixed', top: 'max(12px, env(safe-area-inset-top, 12px))', right: 'max(16px, env(safe-area-inset-right, 16px))', zIndex: 120, display: 'flex', alignItems: 'center', gap: '12px' }}
      >
        {isAdmin && (
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={() => setBroadcastModalOpen(true)}
            title="Broadcast Message"
            style={{ borderRadius: '20px', padding: '6px 14px' }}
          >
            📢 <span className="hide-mobile">Broadcast</span>
          </button>
        )}
        <ThemeToggle />
        <NotificationBell />
      </div>

      <BroadcastModal isOpen={broadcastModalOpen} onClose={() => setBroadcastModalOpen(false)} />

      <main className={`app-main${isKhataRoute ? ' app-main--khata' : ''}`}>
        <div className="app-main-inner">
          <PartyMotivationBanner />
          <Suspense
            fallback={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '60vh',
                }}
              >
                <LoaderDashboard height={30} width={30} />
              </div>
            }
          >
            {children}
          </Suspense>
        </div>
      </main>
    </div>
  );
}

function RequireAuth({ children, adminOnly = false }) {
  const { isAuthenticated, isAdmin, isSuperAdmin, isPersonalKhata } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isPersonalKhata) {
    return <Navigate to="/personal-khata" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (isSuperAdmin && location.pathname === '/') {
    return <Navigate to="/super-admin/pending-admins" replace />;
  }

  return children;
}

function RequireSuperAdminAuth({ children }) {
  const { isAuthenticated, isSuperAdmin } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/** Super admins only use the approval console (and auth / personal khata pages). */
function SuperAdminShell({ children }) {
  const location = useLocation();
  const { user, isSuperAdmin } = useAuth();

  if (user?.status === 'approved' && isSuperAdmin) {
    const p = location.pathname;
    const allowed =
      p.startsWith('/super-admin') ||
      p.startsWith('/login') ||
      p.startsWith('/signup') ||
      p.startsWith('/forgot-password') ||
      p.startsWith('/reset-password') ||
      p.startsWith('/personal-khata');
    if (!allowed) {
      return <Navigate to="/super-admin/pending-admins" replace />;
    }
  }

  return children;
}

/** On each page navigation, refresh app data in the background so pages show the latest
 *  server state without a manual full-page reload. The first render is skipped (initial
 *  bootstrap already loads the data). refreshData is throttled + role-guarded internally. */
function RouteChangeRefresher() {
  const { pathname } = useLocation();
  const { refreshData } = useApp();
  const firstRef = useRef(true);

  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false;
      return;
    }
    // Refresh row data (text) in the background — bill images load lazily per visible row,
    // so navigation stays instant and we never bulk-download receipts on page change.
    refreshData();
  }, [pathname, refreshData]);

  return null;
}

/** Automatically mark notifications as read when their corresponding tab/page is opened. */
function RouteChangeNotificationClearer() {
  const { pathname, search } = useLocation();
  const { isAdmin, isParty, isAuthenticated } = useAuth();
  const { markNotificationsReadByContext } = useApp();

  useEffect(() => {
    if (!isAuthenticated || !markNotificationsReadByContext) return;
    const params = new URLSearchParams(search);
    const lotId = params.get('lotId') || '';

    // If specific lotId in query, mark its notification as read
    if (lotId) {
      markNotificationsReadByContext({ lotId });
    }

    // 1. Admin opens Review Lots
    if (isAdmin && pathname === '/review-lots') {
      markNotificationsReadByContext({ category: 'review_lots', lotId });
    }

    // 2. Admin opens Party Ledger for bill review
    if (isAdmin && pathname === '/party-ledger' && params.get('billReview') === '1') {
      markNotificationsReadByContext({ category: 'bill_revision', lotId });
    }

    // 3. Party user opens Party Ledger (My Lots)
    if (isParty && pathname === '/party-ledger') {
      markNotificationsReadByContext({ category: 'party_ledger', lotId });
    }

    // 4. Party user opens Payments
    if (isParty && pathname === '/payments') {
      markNotificationsReadByContext({ category: 'payments' });
    }
  }, [pathname, search, isAdmin, isParty, isAuthenticated, markNotificationsReadByContext]);

  return null;
}

/** Subtle, non-blocking indicator shown while a background data refresh is in flight. */
function BootstrapErrorBanner() {
  const { bootstrapLoadError, refreshData, initialDataLoading } = useApp();
  if (!bootstrapLoadError || initialDataLoading) return null;
  return (
    <div
      role="alert"
      className="bootstrap-error-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 500,
        background: 'var(--danger, #7f1d1d)',
        color: '#fff',
        padding: '10px 16px',
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <span>Could not load app data. Check your connection.</span>
      <button
        type="button"
        onClick={() => refreshData({ force: true })}
        style={{
          background: '#fff',
          color: 'var(--danger, #7f1d1d)',
          border: 'none',
          borderRadius: 6,
          padding: '4px 12px',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </div>
  );
}

function BackgroundRefreshIndicator() {
  const { backgroundRefreshing } = useApp();
  if (!backgroundRefreshing) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 400,
        background: 'var(--text-primary, #1e293b)',
        color: 'var(--body-bg, #fff)',
        fontSize: 12,
        fontWeight: 600,
        padding: '6px 14px',
        borderRadius: 999,
        boxShadow: '0 6px 20px rgba(15,23,42,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: 'var(--primary-light, #38bdf8)',
          display: 'inline-block',
        }}
      />
      Updating…
    </div>
  );
}

function AppRoutes() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  usePushNotifications({ isAuthenticated });

  return (
    <SuperAdminShell>
      <MarqueeBanner />
      <RouteChangeRefresher />
      <RouteChangeNotificationClearer />
      <BootstrapErrorBanner />
      <BackgroundRefreshIndicator />
      <LotNotificationListener />
      <IosInstallPrompt />
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
            }}
          >
            <LoaderDashboard height={30} width={30} />
          </div>
        }
      >
        <ErrorBoundary>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/personal-khata/account" element={<PersonalKhataAccount />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <Dashboard />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/ghausia"
            element={
              <RequireAuth adminOnly>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <GhausiaCollection />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/party-ledger"
            element={
              <RequireAuth>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <PartyLedger />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/parties"
            element={
              <RequireAuth adminOnly>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <Parties />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/payments"
            element={
              <RequireAuth>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <Payments />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/rate-calculations"
            element={
              <RequireAuth>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <RateCalculations />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth adminOnly>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <UserApprovals />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/review-lots"
            element={
              <RequireAuth adminOnly>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <ReviewLots />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/super-admin/pending-admins"
            element={
              <RequireSuperAdminAuth>
                <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
                  <SuperAdminApprovals />
                </Layout>
              </RequireSuperAdminAuth>
            }
          />
          <Route path="/personal-khata/shared" element={<PersonalKhataShared />} />
          <Route path="/personal-khata/upgrade" element={<UpgradeAccount />} />
          <Route
            path="/personal-khata"
            element={
              <PersonalKhataAccessibleRoute
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            }
          />
          <Route
            path="/personal-khata/contact/:contactId"
            element={
              <PersonalKhataAccessibleRoute
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
    </SuperAdminShell>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AppProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
